/**
 * Die eine IndexedDB dieser Anwendung -- und die eine Stelle, die sie öffnet.
 *
 * **Warum das eine eigene Datei ist.** Der Ausgangskorb öffnete `travel-
 * broadcast` bis hierher selbst, mit Version 1. Stellt man einen zweiten
 * Speicher daneben und öffnet ihn mit Version 2, dann scheitert der
 * Ausgangskorb danach mit `VersionError`: der Browser lehnt eine **kleinere**
 * Version rundheraus ab, und zwar auf demselben Gerät, auf dem gerade
 * ungesendete Nachrichten liegen. Eine Datenbank hat eine Version, nicht je
 * Speicher eine -- also gehört das Öffnen an eine Stelle.
 *
 * Wer einen dritten Speicher braucht, hebt `VERSION` und legt ihn in
 * `aufwerten()` an. Die Aufwertung läuft auf jedem Gerät genau einmal und darf
 * deshalb nichts voraussetzen: sie prüft jeden Speicher einzeln, statt sich auf
 * die alte Version zu verlassen. Vorhandene Inhalte überstehen sie -- genau
 * dafür gibt es sie.
 */

import { WORTLAUT } from '../texte'

const DB = 'travel-broadcast'

/**
 * 1 war der Ausgangskorb allein, 2 kam der Vorrat dazu (#13).
 */
const VERSION = 2

/** Was ohne Netz geschrieben wurde und noch raus muss. */
export const AUSGANG = 'ausgang'

/** Der zuletzt gelesene Stand, damit die App ohne Netz etwas zu zeigen hat. */
export const VORRAT = 'vorrat'

let offen: Promise<IDBDatabase> | null = null

function aufwerten(datenbank: IDBDatabase): void {
  if (!datenbank.objectStoreNames.contains(AUSGANG)) {
    datenbank.createObjectStore(AUSGANG, { keyPath: 'key', autoIncrement: true })
  }
  if (!datenbank.objectStoreNames.contains(VORRAT)) {
    // Ein fester Schlüssel je Eintrag, kein `autoIncrement`: im Vorrat steht
    // immer genau ein Satz, und der wird überschrieben statt angehäuft.
    datenbank.createObjectStore(VORRAT)
  }
}

export function db(): Promise<IDBDatabase> {
  if (offen !== null) return offen

  offen = new Promise<IDBDatabase>((resolve, reject) => {
    const anfrage = indexedDB.open(DB, VERSION)
    anfrage.onupgradeneeded = () => aufwerten(anfrage.result)
    anfrage.onsuccess = () => resolve(anfrage.result)
    anfrage.onerror = () => reject(anfrage.error ?? new Error('IndexedDB'))
    // **Ein zweiter Tab kann die Aufwertung aufhalten.** Er hält die alte
    // Version noch offen; ohne diesen Zweig hinge das Versprechen für immer,
    // und mit ihm hängt bloß der Vorrat -- die Anwendung läuft weiter.
    anfrage.onblocked = () => reject(new Error(WORTLAUT.technik.fehler.indexeddbBelegt))
  })

  // Einmal darum bitten, dass iOS diesen Speicher nicht wegräumt. Safari gibt
  // Website-Daten nach längerer Nichtnutzung frei; für eine App auf dem
  // Home-Bildschirm ist das entschärft, aber fragen kostet nichts.
  void navigator.storage?.persist?.().catch(() => undefined)

  return offen
}

/** Eine Arbeit auf **einem** Speicher, mit Versprechen statt Ereignissen. */
export function lauf<T>(
  store: string,
  modus: IDBTransactionMode,
  arbeit: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return db().then(
    (datenbank) =>
      new Promise<T>((resolve, reject) => {
        const t = datenbank.transaction(store, modus)
        const anfrage = arbeit(t.objectStore(store))
        anfrage.onsuccess = () => resolve(anfrage.result)
        anfrage.onerror = () => reject(anfrage.error ?? new Error('IndexedDB'))
      }),
  )
}
